import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Building2,
    Users,
    CreditCard,
    Wrench,
    FileText,
    Settings,
    LogOut,
    Menu,
    X,
    Search,
    Bell,
    Plus,
    Home,
    CheckCircle2,
    AlertCircle,
    ChevronRight,
    MessageSquare,
    TrendingUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { useHeartbeat } from '../lib/presence';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './DashboardLayout.css';

// Sidebar is grouped so related items sit together and Tenants is
// visually nested under Properties (Portfolio section).
const NAV_GROUPS = [
    {
        label: 'sectionOverview',
        items: [
            { key: 'dashboard', href: '/app/landlord/dashboard', icon: LayoutDashboard, end: true },
        ],
    },
    {
        label: 'sectionPortfolio',
        items: [
            { key: 'properties', href: '/app/landlord/properties', icon: Building2 },
            { key: 'tenants',    href: '/app/landlord/tenants',    icon: Users,    nested: true },
            { key: 'documents',  href: '/app/landlord/documents',  icon: FileText, nested: true },
            { key: 'maintenance', href: '/app/landlord/maintenance', icon: Wrench, nested: true },
        ],
    },
    {
        label: 'sectionFinance',
        items: [
            { key: 'payments',  href: '/app/landlord/payments',           icon: CreditCard },
            { key: 'analytics', href: '/app/landlord/payments/analytics', icon: TrendingUp, nested: true },
        ],
    },
    {
        label: 'sectionWorkspace',
        items: [
            { key: 'chat',     href: '/app/landlord/chat',     icon: MessageSquare },
            { key: 'settings', href: '/app/landlord/settings', icon: Settings },
        ],
    },
];

interface Notif {
    id: string;
    type: string;
    title: string;
    body: string;
    href: string;
    read: boolean;
    created_at: string;
}

export default function DashboardLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [quickAddOpen, setQuickAddOpen] = useState(false);
    const { user, logout } = useAppContext();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Mark this user as online while the dashboard is mounted + tab is visible
    useHeartbeat();

    // ── Notifications ──────────────────────────────────────────────────────
    const [notifs, setNotifs] = useState<Notif[]>([]);
    const [bellOpen, setBellOpen] = useState(false);
    const bellRef = useRef<HTMLDivElement>(null);

    const loadNotifs = async () => {
        if (!user.id) return;
        const { data } = await supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(30);
        setNotifs((data ?? []) as Notif[]);
    };

    useEffect(() => {
        if (!user.id) return;
        loadNotifs();

        // Realtime: push new notifications into state immediately
        const channel = supabase
            .channel(`notifs:landlord:${user.id}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
                (payload) => {
                    setNotifs(prev => [payload.new as Notif, ...prev]);
                }
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [user.id]);

    // Click-outside bell panel
    useEffect(() => {
        if (!bellOpen) return;
        const handler = (e: MouseEvent) => {
            if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
                setBellOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [bellOpen]);

    const unreadCount = notifs.filter(n => !n.read).length;

    const markAllRead = async () => {
        if (!user.id) return;
        const unreadIds = notifs.filter(n => !n.read).map(n => n.id);
        if (!unreadIds.length) return;
        await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
        setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    };

    const handleBellToggle = () => {
        setBellOpen(o => {
            if (!o) markAllRead();
            return !o;
        });
    };

    // ── Profile dropdown ───────────────────────────────────────────────────
    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!profileOpen) return;
        const handler = (e: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [profileOpen]);

    // In-app cursor glow
    useEffect(() => {
        const glow = document.getElementById('appCursorGlow');
        if (!glow) return;
        const onMove = (e: MouseEvent) => {
            glow.style.left = e.clientX + 'px';
            glow.style.top = e.clientY + 'px';
        };
        window.addEventListener('mousemove', onMove);
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const initials = user.name
        ? user.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()
        : (user.email?.[0] || 'L').toUpperCase();

    return (
        <div className="dashboard">
            <div id="appCursorGlow" className="app-cursor-glow" />
            {/* Mobile header */}
            <div className="mobile-header">
                <button className="mobile-header__toggle" onClick={() => setSidebarOpen(true)}>
                    <Menu size={22} />
                </button>
                <span style={{ color: 'var(--color-text)', fontWeight: 700, marginLeft: 12, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <img src="/rentnova-mark.png" alt="" width={22} height={22} style={{ display: 'block', objectFit: 'contain', transform: 'translateY(-3px)' }} />
                    RentNova
                </span>
            </div>

            {/* Overlay */}
            {sidebarOpen && (
                <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Sidebar */}
            <aside className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>
                <div className="sidebar__logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
                    <img
                        src="/rentnova-mark.png"
                        alt="RentNova"
                        width={30}
                        height={30}
                        style={{ display: 'block', objectFit: 'contain', flexShrink: 0, transform: 'translateY(-4px)' }}
                    />
                    <span>RentNova</span>
                    {sidebarOpen && (
                        <button
                            onClick={() => setSidebarOpen(false)}
                            style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca0b3', cursor: 'pointer' }}
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>

                <nav className="sidebar__nav">
                    {NAV_GROUPS.map((group) => (
                        <div key={group.label} className="sidebar__group">
                            <div className="sidebar__section-label">{t(`nav.${group.label}`)}</div>
                            {group.items.map((item) => (
                                <NavLink
                                    key={item.href}
                                    to={item.href}
                                    end={'end' in item ? item.end : false}
                                    className={({ isActive }) =>
                                        `sidebar__link${isActive ? ' sidebar__link--active' : ''}${'nested' in item && item.nested ? ' sidebar__link--nested' : ''}`
                                    }
                                    onClick={() => setSidebarOpen(false)}
                                >
                                    <item.icon size={'nested' in item && item.nested ? 17 : 20} />
                                    {t(`nav.${item.key}`)}
                                </NavLink>
                            ))}
                        </div>
                    ))}
                </nav>

                <div style={{ margin: '12px', marginTop: 'auto' }}>
                    {/* User info card */}
                    <div className="sidebar__user" style={{ margin: 0, marginBottom: '4px' }}>
                        <div className="sidebar__user-avatar">
                            {user.name.substring(0, 2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div className="sidebar__user-name" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {user.name}
                            </div>
                            <div className="sidebar__user-email" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {user.email}
                            </div>
                            <div style={{ marginTop: '4px', fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase', color: '#6366f1', background: 'rgba(99,102,241,0.15)', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                {user.plan} {t('common.plan')}
                            </div>
                        </div>
                    </div>
                    {/* Sign out — full-width, clearly labelled */}
                    <button
                        onClick={handleLogout}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: '7px', width: '100%', padding: '8px 12px',
                            background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.15)',
                            borderRadius: '10px', cursor: 'pointer',
                            color: '#b91c1c', fontSize: '0.75rem', fontWeight: 600,
                            transition: 'all 0.15s', letterSpacing: '0.01em',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.10)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#b91c1c'; e.currentTarget.style.background = 'rgba(239,68,68,0.05)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.15)'; }}
                    >
                        <LogOut size={13} />
                        {t('common.signOut')}
                    </button>
                </div>
            </aside>

            {/* Main Container */}
            <div className="main-content">
                {/* Topbar */}
                <header className="topbar">
                    <div className="topbar__search">
                        <Search size={16} />
                        <input type="text" placeholder={t('common.searchPlaceholder')} />
                    </div>

                    <div className="topbar__actions">

                        {/* ── Language switcher — discreet, top of app ── */}
                        <LanguageSwitcher variant="topbar" />

                        {/* ── Bell ── */}
                        <div ref={bellRef} style={{ position: 'relative' }}>
                            <button
                                className="topbar__icon-btn"
                                onClick={handleBellToggle}
                                style={{ position: 'relative' }}
                                title={t('nav.notifications')}
                            >
                                <Bell size={18} />
                                {unreadCount > 0 && (
                                    <span style={{
                                        position: 'absolute', top: 4, right: 4,
                                        width: 8, height: 8, borderRadius: '50%',
                                        background: '#ef4444',
                                        border: '1.5px solid var(--color-bg)',
                                    }} />
                                )}
                            </button>

                            {bellOpen && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                    width: 320, maxWidth: 'calc(100vw - 2rem)', maxHeight: 420, overflowY: 'auto',
                                    background: 'var(--color-bg)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 14,
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                                    zIndex: 200,
                                }}>
                                    <div style={{ padding: '0.875rem 1rem 0.5rem', borderBottom: '1px solid var(--color-border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{t('nav.notifications')}</span>
                                        {notifs.length > 0 && (
                                            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>{notifs.length} item{notifs.length !== 1 ? 's' : ''}</span>
                                        )}
                                    </div>

                                    {notifs.length === 0 ? (
                                        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: '0.875rem' }}>
                                            {t('nav.noNotifications')}
                                        </div>
                                    ) : (
                                        notifs.map(n => (
                                            <Link
                                                key={n.id}
                                                to={n.href}
                                                onClick={() => setBellOpen(false)}
                                                style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.875rem 1rem', textDecoration: 'none', color: 'inherit', borderBottom: '1px solid var(--color-border-subtle)' }}
                                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                            >
                                                <div style={{
                                                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: (n.type === 'document_signed' || n.type === 'ticket_status') ? 'rgba(34,197,94,0.1)' : n.type === 'new_message' ? 'rgba(99,102,241,0.1)' : 'rgba(245,158,11,0.1)',
                                                }}>
                                                    {(n.type === 'document_signed' || n.type === 'ticket_status')
                                                        ? <CheckCircle2 size={16} color="#16a34a" />
                                                        : n.type === 'new_message'
                                                            ? <MessageSquare size={16} color="#6366f1" />
                                                            : <AlertCircle size={16} color="#d97706" />}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: n.read ? 500 : 700, fontSize: '0.8125rem', color: 'var(--color-text)' }}>{n.title}</div>
                                                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</div>
                                                </div>
                                                <ChevronRight size={14} color="var(--color-text-tertiary)" style={{ flexShrink: 0, marginTop: 3 }} />
                                            </Link>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        {/* ── Quick Add ── */}
                        <div className="relative">
                            <button
                                className="btn btn-primary btn-sm topbar__add-btn"
                                onClick={() => setQuickAddOpen(!quickAddOpen)}
                            >
                                <Plus size={16} />
                                <span>{t('nav.quickAdd')}</span>
                            </button>

                            {quickAddOpen && (
                                <>
                                    <div className="dropdown-overlay" onClick={() => setQuickAddOpen(false)} />
                                    <div className="dropdown-menu">
                                        <button className="dropdown-item" onClick={() => { setQuickAddOpen(false); navigate('/app/landlord/properties'); }}>
                                            <Home size={16} /> {t('nav.addProperty')}
                                        </button>
                                        <button className="dropdown-item" onClick={() => { setQuickAddOpen(false); navigate('/app/landlord/tenants'); }}>
                                            <Users size={16} /> {t('nav.addTenant')}
                                        </button>
                                        <button className="dropdown-item" onClick={() => { setQuickAddOpen(false); navigate('/app/landlord/maintenance'); }}>
                                            <Wrench size={16} /> {t('nav.newTicket')}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ── Profile avatar ── */}
                        <div ref={profileRef} style={{ position: 'relative' }}>
                            <button
                                onClick={() => setProfileOpen(o => !o)}
                                title="Account"
                                style={{
                                    width: 36, height: 36, borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                    border: profileOpen ? '2px solid #16a34a' : '2px solid transparent',
                                    color: '#fff', fontWeight: 700, fontSize: '0.8125rem',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    letterSpacing: '0.02em', flexShrink: 0,
                                    boxShadow: profileOpen ? '0 0 0 3px rgba(99,102,241,0.25)' : 'none',
                                    transition: 'box-shadow 0.15s, border-color 0.15s',
                                }}
                            >
                                {initials}
                            </button>

                            {profileOpen && (
                                <div style={{
                                    position: 'absolute', top: 'calc(100% + 10px)', right: 0,
                                    width: 240,
                                    background: 'var(--color-bg)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 14,
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
                                    zIndex: 200, overflow: 'hidden',
                                }}>
                                    {/* User info */}
                                    <div style={{ padding: '1rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {user.name || 'Landlord'}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {user.email}
                                        </div>
                                        <span style={{
                                            display: 'inline-block', marginTop: '0.5rem',
                                            fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.06em',
                                            textTransform: 'uppercase', color: '#16a34a',
                                            background: 'rgba(22,163,74,0.10)', borderRadius: 6,
                                            padding: '2px 8px',
                                        }}>
                                            {user.plan} {t('common.plan')}
                                        </span>
                                    </div>

                                    {/* Links */}
                                    <div style={{ padding: '0.375rem 0' }}>
                                        {[
                                            { label: t('nav.settings'), icon: Settings, href: '/app/landlord/settings' },
                                            { label: t('nav.documents'), icon: FileText, href: '/app/landlord/documents' },
                                            { label: t('nav.properties'), icon: Building2, href: '/app/landlord/properties' },
                                        ].map(item => (
                                            <Link
                                                key={item.href}
                                                to={item.href}
                                                onClick={() => setProfileOpen(false)}
                                                style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.625rem 1rem', textDecoration: 'none', color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: 500 }}
                                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                            >
                                                <item.icon size={15} color="var(--color-text-tertiary)" />
                                                {item.label}
                                            </Link>
                                        ))}
                                    </div>

                                    {/* Sign out */}
                                    <div style={{ borderTop: '1px solid var(--color-border-subtle)', padding: '0.5rem' }}>
                                        <button
                                            onClick={handleLogout}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', width: '100%', padding: '0.75rem 1rem', background: 'rgba(239,68,68,0.06)', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.875rem', fontWeight: 700, textAlign: 'left', borderRadius: '10px' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.12)')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.06)')}
                                        >
                                            <LogOut size={15} />
                                            {t('common.signOut')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </header>

                <div className="page-wrapper">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
